// Live's "Extract" on the brand and product forms (#249, spec G15-extract-from-url): the job
// reads the page in the worker; its one JSON output is the answer the form fills from. Live
// waits on one request; ours starts the job, follows it as a job's page does (stream events,
// then the fallback timer), and reads its output once, as the script tools read theirs.
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import type { z } from "zod";

import { fetchSignedFile } from "../../api/download";
import { useGetJob, useStartJob } from "../../api/generated/endpoints/generation/generation";
import type { JobRequest } from "../../api/generated/model";
import { canChange, jobRefetchInterval } from "../../platform/jobs/refetch";
import { useStreamStatus } from "../../platform/stream-status";

/**
 * Live's form check on the address (`brand-form`): a protocol is optional and the host has a dot;
 * anything else is "Please enter a valid URL".
 */
export function extractable(text: string): boolean {
  const typed = text.trim();
  if (typed === "" || /\s/.test(typed)) return false;
  try {
    const host = new URL(/^https?:\/\//i.test(typed) ? typed : `https://${typed}`).hostname;
    return host.includes(".");
  } catch {
    return false;
  }
}

export interface ExtractOptions<A> {
  teamId: string;
  /** The brand whose jobs and library it runs in; null where the team has none yet. */
  brandId: string | null;
  answer: z.ZodType<A>;
  /** The answer, once, as the form fills from it. */
  onAnswer: (answer: A) => void | Promise<void>;
}

export interface Extract {
  /** The job runs, or the form fills from its answer. */
  running: boolean;
  /** The last extraction failed: live's one sentence shows. */
  failed: boolean;
  start: (request: JobRequest) => void;
}

export function useExtract<A>({ teamId, brandId, answer, onAnswer }: ExtractOptions<A>): Extract {
  const stream = useStreamStatus();
  const [jobId, setJobId] = useState<string | null>(null);
  const start = useStartJob({ mutation: { onSuccess: (job) => setJobId(job.id) } });
  const job = useGetJob(teamId, jobId ?? "", {
    query: {
      enabled: jobId !== null,
      refetchInterval: (query) => jobRefetchInterval(query.state.data?.status, stream),
    },
  });
  const status = jobId === null ? undefined : job.data?.status;
  const url = status === "succeeded" ? (job.data?.outputs[0]?.url ?? null) : null;
  const read = useQuery({
    queryKey: ["extract-answer", jobId],
    enabled: url !== null,
    queryFn: async ({ signal }) =>
      answer.parse(JSON.parse(await (await fetchSignedFile(url!, signal)).text())),
    staleTime: Infinity,
    retry: false,
  });
  // The answer lands once per job, through the latest callback.
  const deliver = useRef(onAnswer);
  useEffect(() => {
    deliver.current = onAnswer;
  });
  const fill = useMutation({ mutationFn: (found: A) => Promise.resolve(deliver.current(found)) });
  const { mutate: fillWith } = fill;
  const delivered = useRef<string | null>(null);
  const got = read.data;
  useEffect(() => {
    if (jobId === null || got === undefined || delivered.current === jobId) return;
    delivered.current = jobId;
    fillWith(got);
  }, [jobId, got, fillWith]);
  const ended = status !== undefined && !canChange(status);
  const jobFailed =
    jobId !== null && ((ended && (status !== "succeeded" || url === null)) || read.isError);
  const filled = fill.isSuccess || fill.isError;
  return {
    running: start.isPending || (jobId !== null && !jobFailed && !filled),
    failed: start.isError || jobFailed || fill.isError,
    start: (request) => {
      if (brandId === null) return;
      setJobId(null);
      fill.reset();
      start.mutate({
        teamId,
        brandId,
        data: request,
        headers: { "Idempotency-Key": crypto.randomUUID() },
      });
    },
  };
}
